import { ChangeDetectionStrategy, Component, ErrorHandler } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { loadMessages, locale } from 'devextreme/localization';

/*
    !!! DO NOT REMOVE THE IMPORT BELOW !!!
    In order to track uncaught exceptions using ErrorService, it is required to import the specified extension
    https://learn.microsoft.com/it-it/azure/azure-monitor/app/javascript-framework-extensions?tabs=angular#track-exceptions
*/
import { DefaultValueAccessor } from '@angular/forms';
import '@microsoft/applicationinsights-analytics-js';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import config from 'devextreme/core/config';
import dxDataGrid from 'devextreme/ui/data_grid';
import { filter } from 'rxjs';
import itMessages from '../../public/devexpress/it.json';
import { licenseKey } from '../devextreme-license';
import { AlertComponent } from './core/alert/alert.component';
import { MonitoringService } from './core/services/monitoring.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, AlertComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: ErrorHandler,
            useClass: ApplicationinsightsAngularpluginErrorService,
        },
    ],
})
export class AppComponent {
    /**
     * Constructor
     */
    constructor(
        private transloco: TranslocoService,
        /**
         * Used for Azure app insights. Do not remove its injection even if it seems unused. More info on the comment at the top of the file.
         */
        private monitoringService: MonitoringService
    ) {
        this.initializeDevexpress();

        this.CustomizeValueAccessorsBehaviour();

        //As many parts of the UI do not change directly when changing language, trigger a manual refresh when changing language.
        //The language is stored on localstorage, so the app remembers the switched language even when refreshing.
        this.refreshOnLanguageChange();
    }

    /**
     * Sets up custom behaviour for all value accessors (used on inputs)
     * - empty or all spaces strings are converted to null values
     * - values are trimmed
     * @constructor
     */
    CustomizeValueAccessorsBehaviour() {
        DefaultValueAccessor.prototype.registerOnChange = function (fn: (_: string | null) => void): void {
            this.onChange = (value: string | null) => {
                fn(value?.trim() === '' ? null : value.trim());
            };
        };
    }

    initializeDevexpress() {
        config({ licenseKey });
        loadMessages(itMessages);
        // The .dx-datagrid-readonly-cols rule (styles.scss) detects editable cells through
        // aria-roledescription='Editable', which DevExtreme takes from this localized message.
        // Keep it untranslated so the rule works in every language.
        loadMessages({ it: { 'dxDataGrid-ariaEditableCell': 'Editable' } });
        locale(this.transloco.getActiveLang());
        dxDataGrid.defaultOptions({
            options: {
                scrolling: {
                    useNative: true, //Allows to scroll using the mouse wheel or sliding the thumb on a touchscreen
                },
                editing: {
                    texts: {
                        confirmDeleteMessage: '', //Removes the delete confirmation popup
                    },
                },
                grouping: {
                    contextMenuEnabled: false,
                },
            },
        });
    }

    private refreshOnLanguageChange() {
        this.transloco.events$.pipe(filter((e) => e.type == 'langChanged')).subscribe(() => {
            //Simply refresh the page, we don't need to unsubscribe as the app will be destroyed.
            window.location.reload();
        });
    }
}
